import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { useStickyNotification } from '@hotjar/navigation';
import { SessionStorage } from '@hotjar/insights-common/lib/storage-factory';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';
import type { Site } from '@hotjar/site';
import { useCurrentSite } from '@hotjar/site';
import { UpsellBanner } from '../toolkit/UpsellBanner';
import { useSalesBookingForm } from '../../hooks';
import type { SalesSource } from '../../utils/salesLinks/salesLinks';

const Container = styled.div``;

export const getUpsellBannerKey = (id: number | undefined): string =>
  `coverageUpsellBannerDismissed-${id}`;

export enum TRAFFIC_UPSELL_BANNER {
  RECORDINGS = 'RECORDINGS',
  DASHBOARD = 'DASHBOARD',
  TRENDS = 'TRENDS',
  FUNNELS = 'FUNNELS',
}

type TYPE_CONTENT = {
  referrer: KnownReferrer;
  labelKey: string;
  salesSource: SalesSource;
};

export const TRAFFIC_UPSELL_BANNER_TYPE_DATA: { [key in TRAFFIC_UPSELL_BANNER]: TYPE_CONTENT } = {
  RECORDINGS: {
    referrer: 'recordings_upsell_banner' as KnownReferrer,
    labelKey: 'statement-recordings',
    salesSource: 'RecordingsBanner',
  },
  DASHBOARD: {
    referrer: 'dashboard_top_banner_upgrade_link' as KnownReferrer,
    labelKey: 'statement-dashboard',
    salesSource: 'DashboardBanner',
  },
  TRENDS: {
    referrer: 'trends_top_banner_upgrade_link' as KnownReferrer,
    labelKey: 'statement-dashboard',
    salesSource: 'TrendsBanner',
  },
  FUNNELS: {
    referrer: 'funnels_top_banner_upgrade_link' as KnownReferrer,
    labelKey: 'statement-dashboard',
    salesSource: 'FunnelsBanner',
  },
};

type UpsellData = {
  referrer: KnownReferrer;
  site: Site;
};

interface Props {
  upsell: (({ referrer, site }: UpsellData) => Promise<void>) | undefined;
  isHidden?: boolean;
  type: TRAFFIC_UPSELL_BANNER;
}

export const TrafficUpsellBanner = ({ upsell, isHidden, type }: Props): JSX.Element | null => {
  const { t } = useTranslation('common');
  const { notifications } = useStickyNotification();
  const { openSalesBookingForm } = useSalesBookingForm();
  const { coveragePercentage } = useTrafficCoverage();
  const { data: site } = useCurrentSite();

  const [isDismissed, setIsDismissed] = useState(
    SessionStorage.get(getUpsellBannerKey(site?.id), false),
  );

  const notificationsArray = Object.keys(notifications);

  const handleDismiss = useCallback(() => {
    setIsDismissed(true);
    SessionStorage.set(getUpsellBannerKey(site?.id), true);
  }, [site?.id]);

  // Check if we have already dismissed this banner
  if (isDismissed || Boolean(isHidden)) {
    return null;
  }

  // If we have other notifications showing on the page, do not render
  // the upsell banner.
  if (notificationsArray.length) {
    return null;
  }

  // If our traffic coverage level is medium or above (>50%), do not
  // render this upsell banner
  if (coveragePercentage >= 50) {
    return null;
  }

  if (!site) return null;

  const { referrer, labelKey, salesSource } = TRAFFIC_UPSELL_BANNER_TYPE_DATA[type];

  return (
    <Container data-testid="upsell-banner">
      <UpsellBanner
        primaryAction={{
          labelText: t('UpsellBanner.book-call'),
          handleClick: () => openSalesBookingForm(salesSource),
        }}
        secondaryAction={{
          labelText: t('UpsellBanner.upgrade'),
          handleClick: () => upsell?.({ referrer, site }),
        }}
        onDismiss={handleDismiss}
      >
        {t(`UpsellBanner.${labelKey}`, { coveragePercentage })}
      </UpsellBanner>
    </Container>
  );
};
